<template>
  <div class="ss-wrapper" @keydown="onKeydown" ref="root">
    <div
      class="ss-control"
      @click="toggleOpen($event)"
      :class="{ open: isOpen, multiple: multiple }"
    >
      <div v-if="multiple && selectedOptions.length" class="ss-tags">
        <span
          v-for="(opt, i) in selectedOptions"
          :key="getOptionKey(opt, i)"
          class="ss-tag"
        >
          {{ getLabel(opt) }}
          <button type="button" class="ss-tag-remove" @mousedown.prevent="removeOption(opt)">×</button>
        </span>
      </div>
      <input
        ref="input"
        type="search"
        :placeholder="inputPlaceholder"
        v-model="search"
        :readonly="!searchable"
        @focus="onFocus"
        @input="onInput"
        class="ss-input"
      />
      <span v-if="loading" class="ss-loading">...</span>
      <span v-else class="ss-arrow">▾</span>
    </div>

    <ul v-if="isOpen" class="ss-dropdown">
      <li v-if="loading" class="ss-no">Yükleniyor...</li>
      <li v-else-if="filtered.length === 0" class="ss-no">Sonuç Bulunamadı</li>
      <li
        v-for="(opt, idx) in filtered"
        :key="getOptionKey(opt, idx)"
        :class="{
          'is-active': idx === highlightIndex,
          'is-selected': isSelected(opt)
        }"
        @mouseenter="highlightIndex = idx"
        @mousedown.prevent="selectOption(opt)"
      >
        <slot name="option" :option="opt">
          <span v-if="multiple" class="ss-check">{{ isSelected(opt) ? '✓' : '' }}</span>
          {{ getLabel(opt) }}
        </slot>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'SearchableSelect',
  props: {
    value: null,
    options: { type: Array, default: () => [] },
    label: { type: String, default: 'name' },
    valueKey: { type: String, default: null },
    reduceFn: { type: Function, default: null },
    filterFn: { type: Function, default: null },
    placeholder: { type: String, default: '' },
    debounce: { type: Number, default: 150 },
    searchable: { type: Boolean, default: true },
    multiple: { type: Boolean, default: false },
    remote: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    limit: { type: Number, default: 200 }
  },
  data() {
    return {
      isOpen: false,
      search: '',
      highlightIndex: 0,
      debounceTimer: null
    };
  },
  computed: {
    inputPlaceholder() {
      if (this.multiple && this.selectedOptions.length) return '';
      if (!this.isOpen && this.search) return '';
      return this.placeholder;
    },
    selectedOptions() {
      if (!this.multiple) return [];
      const values = Array.isArray(this.value) ? this.value : [];
      return values
        .map(v => this.findOptionByValue(v))
        .filter(Boolean);
    },
    filtered() {
      if (this.remote) return this.options;
      const list = this.options || [];
      if (!this.searchable || !this.isOpen) return list.slice(0, this.limit);

      const q = (this.search || '').toString().toLowerCase().trim();
      if (!q) return list.slice(0, this.limit);

      return list.filter(o => {
        if (this.filterFn) return this.filterFn(o, q);
        return this.getLabel(o).toString().toLowerCase().indexOf(q) !== -1;
      });
    }
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        if (!this.isOpen) this.syncSearchFromValue();
      }
    },
    options() {
      this.highlightIndex = 0;
      if (!this.isOpen) this.syncSearchFromValue();
    },
    isOpen(open) {
      if (open) {
        this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
      } else {
        this.syncSearchFromValue();
      }
    }
  },
  methods: {
    getLabel(opt) {
      if (!opt) return '';
      if (typeof opt === 'string') return opt;
      return opt[this.label] !== undefined ? opt[this.label] : (opt.label || opt.name || '');
    },
    getValue(opt) {
      if (this.reduceFn) return this.reduceFn(opt);
      if (this.valueKey) return opt[this.valueKey];
      return opt;
    },
    getOptionKey(opt, idx) {
      if (opt == null) return 'idx-' + idx;
      if (typeof opt !== 'object') return String(opt);

      const reduced = this.getValue(opt);
      if (typeof reduced === 'string' || typeof reduced === 'number') return reduced;

      if (reduced && typeof reduced === 'object' && reduced.id != null) return reduced.id;

      if (opt.id != null) return opt.id;
      if (this.valueKey && opt[this.valueKey] != null) return opt[this.valueKey];

      const label = this.getLabel(opt);
      if (label) return 'lbl-' + label + '-' + idx;

      return 'idx-' + idx;
    },
    isSameValue(a, b) {
      if (a === b) return true;
      if (a == null || b == null) return false;
      if (typeof a === 'object' && typeof b === 'object') {
        const ak = this.valueKey || 'id';
        if (a[ak] !== undefined && b[ak] !== undefined) return a[ak] === b[ak];
      }
      return false;
    },
    findOptionByValue(val) {
      if (val == null) return null;
      return this.options.find(o => {
        const ov = this.getValue(o);
        return this.isSameValue(ov, val) || ov === val;
      }) || null;
    },
    syncSearchFromValue() {
      if (this.multiple) {
        this.search = '';
        return;
      }
      const found = this.findOptionByValue(this.value);
      if (found) {
        this.search = this.getLabel(found);
        return;
      }
      if (typeof this.value === 'string' || typeof this.value === 'number') {
        this.search = String(this.value);
        return;
      }
      this.search = '';
    },
    emitSearch() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.highlightIndex = 0;
        if (this.remote) this.$emit('search', this.search);
      }, this.debounce);
    },
    onInput() {
      if (!this.searchable) return;
      if (!this.isOpen) this.open();
      this.emitSearch();
    },
    onFocus() {
      if (!this.isOpen) this.open();
      if (this.searchable && !this.multiple) this.search = '';
    },
    open() {
      this.isOpen = true;
      if (this.searchable && !this.multiple) this.search = '';
      if (this.remote) this.$emit('search', this.search || '');
    },
    close() {
      this.isOpen = false;
    },
    toggleOpen(e) {
      const input = this.$refs.input;
      if (input && e && (e.target === input || input.contains(e.target))) {
        if (!this.isOpen) this.open();
        return;
      }
      this.isOpen = !this.isOpen;
    },
    isSelected(opt) {
      const v = this.getValue(opt);
      if (this.multiple) {
        const values = Array.isArray(this.value) ? this.value : [];
        return values.some(item => this.isSameValue(item, v));
      }
      return this.isSameValue(this.value, v);
    },
    selectOption(opt) {
      const v = this.getValue(opt);

      if (this.multiple) {
        const current = Array.isArray(this.value) ? [...this.value] : [];
        const index = current.findIndex(item => this.isSameValue(item, v));
        if (index === -1) current.push(v);
        else current.splice(index, 1);
        this.$emit('input', current);
        this.search = '';
        if (this.remote) this.$emit('search', '');
        return;
      }

      this.$emit('input', v);
      this.search = this.getLabel(opt);
      this.close();
    },
    removeOption(opt) {
      if (!this.multiple) return;
      const v = this.getValue(opt);
      const current = Array.isArray(this.value) ? [...this.value] : [];
      const next = current.filter(item => !this.isSameValue(item, v));
      this.$emit('input', next);
    },
    onKeydown(e) {
      const navKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'];
      if (!navKeys.includes(e.key)) return;

      e.preventDefault();
      e.stopPropagation();

      if (!this.isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
        this.open();
        return;
      }
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (!this.isOpen) return;

      if (e.key === 'ArrowDown') {
        this.highlightIndex = Math.min(this.highlightIndex + 1, this.filtered.length - 1);
        this.scrollTo();
        return;
      }
      if (e.key === 'ArrowUp') {
        this.highlightIndex = Math.max(this.highlightIndex - 1, 0);
        this.scrollTo();
        return;
      }
      if (e.key === 'Enter') {
        const opt = this.filtered[this.highlightIndex];
        if (opt) this.selectOption(opt);
      }
    },
    scrollTo() {
      this.$nextTick(() => {
        const ul = this.$el.querySelector('.ss-dropdown');
        const li = ul && ul.children[this.highlightIndex];
        if (li && ul) {
          const top = li.offsetTop;
          const h = ul.clientHeight;
          if (top < ul.scrollTop) ul.scrollTop = top;
          else if (top + li.clientHeight > ul.scrollTop + h) ul.scrollTop = top - h + li.clientHeight;
        }
      });
    }
  },
  mounted() {
    const onDoc = (e) => {
      if (!this.$el.contains(e.target)) this.close();
    };
    document.addEventListener('click', onDoc);
    this._cleanup = () => document.removeEventListener('click', onDoc);
    this.syncSearchFromValue();
  },
  beforeDestroy() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this._cleanup && this._cleanup();
  }
};
</script>

<style scoped>
.ss-wrapper { position: relative; width: 100%; }
.ss-control {
  border-radius: 10px;
  border: 1px solid rgba(17, 24, 39, 0.07);
  background: #fff;
  box-shadow: 0 4px 14px rgba(14, 82, 163, 0.04);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 2px;
  cursor: text;
  min-height: 39.5px;
}
.ss-control.open { box-shadow: 0 14px 48px rgba(13, 110, 253, 0.06); }
.ss-control.multiple { padding-top: 4px; padding-bottom: 4px; }
.ss-tags { display: flex; flex-wrap: wrap; gap: 4px; padding: 2px 4px; }
.ss-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(13, 110, 253, 0.08);
  color: var(--bs-primary, #0d6efd);
  border-radius: 6px;
  padding: 2px 6px;
  font-size: 0.75rem;
}
.ss-tag-remove {
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  font-size: 0.9rem;
}
.ss-input {
  border: none;
  outline: none;
  flex: 1;
  min-width: 60px;
  font-size: 0.8125rem;
  padding: 6px 8px;
  background: transparent;
  height: 35px;
}
.ss-arrow { margin-right: 8px; color: #111827; }
.ss-loading { margin-right: 8px; color: #6b7280; font-size: 0.75rem; }
.ss-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 1050;
  margin-top: 6px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid rgba(17, 24, 39, 0.07);
  box-shadow: 0 8px 24px rgba(14, 82, 163, 0.08);
  max-height: 260px;
  overflow: auto;
  padding: 6px 0;
}
.ss-dropdown li {
  list-style: none;
  padding: 8px 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ss-dropdown li.is-active { background: rgba(13, 110, 253, 0.06); color: var(--bs-primary, #0d6efd); }
.ss-dropdown li.is-selected { font-weight: 500; }
.ss-check { width: 14px; color: var(--bs-primary, #0d6efd); }
.ss-no { padding: 10px 14px; color: #111827; }
</style>
